import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Collapse,
  Divider,
  ListItemButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const LogsFilter = ({
  dateRange,
  onDateApply,
  onDateRangeChange,
  levelFilter,
  onLevelApply,
  postbackIdFilter,
  onPostbackIdApply,
  clkidFilter,
  onClkidApply,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [pendingDateRange, setPendingDateRange] = useState([
    { startDate: dateRange[0].startDate, endDate: dateRange[0].endDate, key: 'selection' },
  ]);
  const [pendingLevel, setPendingLevel] = useState(levelFilter);
  const [pendingPostbackId, setPendingPostbackId] = useState(postbackIdFilter);
  const [pendingClkid, setPendingClkid] = useState(clkidFilter);

  const toggle = (section) =>
    setExpanded((prev) => (prev === section ? null : section));

  const chevronSx = (section) => ({
    flexShrink: 0,
    transform: expanded === section ? 'rotate(180deg)' : 'none',
    transition: 'transform 0.2s',
  });

  const handleOpen = (e) => {
    setPendingDateRange([
      { startDate: dateRange[0].startDate, endDate: dateRange[0].endDate, key: 'selection' },
    ]);
    setPendingLevel(levelFilter);
    setPendingPostbackId(postbackIdFilter);
    setPendingClkid(clkidFilter);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleDateChange = (item) => {
    const selection = item.selection;
    setPendingDateRange([selection]);
    onDateRangeChange([selection]);
  };

  const handleApplyDate = () => {
    onDateApply();
    handleClose();
  };

  const handleApplyLevel = () => {
    onLevelApply(pendingLevel);
    handleClose();
  };

  const handleApplyPostbackId = () => {
    onPostbackIdApply(pendingPostbackId);
    handleClose();
  };

  const handleApplyClkid = () => {
    onClkidApply(pendingClkid);
    handleClose();
  };

  return (
    <>
      <Button
        variant="text"
        startIcon={<FilterListIcon />}
        onClick={handleOpen}
      >
        Filter
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper elevation={0}>
          <ListItemButton onClick={() => toggle('date')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body2">Updated At</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('date')} />
          </ListItemButton>
          <Collapse in={expanded === 'date'}>
            <Box sx={{ pb: 1 }}>
              <DateRange
                ranges={pendingDateRange}
                onChange={handleDateChange}
                maxDate={new Date()}
              />
              <Box display="flex" justifyContent="flex-end" px={2} pb={1}>
                <Button size="small" variant="contained" onClick={handleApplyDate}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>

          <Divider />

          <ListItemButton onClick={() => toggle('level')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LabelOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Level</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('level')} />
          </ListItemButton>
          <Collapse in={expanded === 'level'}>
            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={pendingLevel}
                onChange={(e) => setPendingLevel(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
              <Box display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleApplyLevel}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>

          <Divider />

          {/* Postback ID */}
          <ListItemButton onClick={() => toggle('postbackid')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LinkIcon fontSize="small" color="action" />
              <Typography variant="body2">Postback ID</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('postbackid')} />
          </ListItemButton>
          <Collapse in={expanded === 'postbackid'}>
            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Postback ID"
                value={pendingPostbackId}
                onChange={(e) => setPendingPostbackId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyPostbackId()}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleApplyPostbackId}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>

          <Divider />

          <ListItemButton onClick={() => toggle('clickid')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <FingerprintIcon fontSize="small" color="action" />
              <Typography variant="body2">Click ID</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('clickid')} />
          </ListItemButton>
          <Collapse in={expanded === 'clickid'}>
            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Click ID"
                value={pendingClkid}
                onChange={(e) => setPendingClkid(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyClkid()}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleApplyClkid}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
          <Divider />
        </Paper>
      </Popover>
    </>
  );
};

export default LogsFilter;
